import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { apiService } from '../services/api';
import './Registration.css';

const Registration = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    mobile_no: '',
    dob: '',
    country: 'IN',
    gender: 'Male',
    referral_by: '',
    android_version: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [consentAccepted, setConsentAccepted] = useState(false);

  // Redirect if already logged in
  React.useEffect(() => {
    const userId = sessionStorage.getItem('userId');
    const userName = sessionStorage.getItem('userName');
    if (userId && userName) {
      navigate(`/dashboard/${encodeURIComponent(userName)}`);
    }
  }, [navigate]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const validateForm = () => {
    const { name, email, mobile_no, dob, country, gender, referral_by, android_version } = formData;

    if (!name.trim()) {
      setError('Name is required');
      return false;
    }

    if (!email.trim() || !/\S+@\S+\.\S+/.test(email)) {
      setError('Valid email is required');
      return false;
    }

    if (!mobile_no.trim() || !/^\d{10,15}$/.test(mobile_no)) {
      setError('Valid mobile number is required (10-15 digits)');
      return false;
    }

    if (!dob) {
      setError('Date of birth is required');
      return false;
    }

    if (!country.trim()) {
      setError('Country is required');
      return false;
    }

    if (!gender.trim()) {
      setError('Gender is required');
      return false;
    }

    if (!referral_by.trim()) {
      setError('Referral is required');
      return false;
    }

    if (!android_version.trim()) {
      setError('Android version is required');
      return false;
    }

    if (!consentAccepted) {
      setError('You must accept the consent agreement to register');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const response = await apiService.registerUser(formData);
      console.log('Registration successful:', response);
      const userId = response.user_id;
      const userName = response.name;
      // Save ID and Name to session storage so Dashboard knows which user it is
      sessionStorage.setItem('userId', String(userId));
      sessionStorage.setItem('userName', userName);
      // Go directly to dashboard — no need to login separately
      navigate(`/dashboard/${encodeURIComponent(userName)}`);
    } catch (err) {
      console.error('Registration error:', err);
      setError(err.response?.data?.error || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="registration-container">
      <h2>User Registration</h2>
      <form onSubmit={handleSubmit} className="registration-form">
        <div className="form-group">
          <label htmlFor="name">Full Name:</label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            placeholder="Enter your full name"
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="email">Email:</label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            placeholder="Enter your email address"
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="mobile_no">Mobile Number:</label>
          <input
            type="tel"
            id="mobile_no"
            name="mobile_no"
            value={formData.mobile_no}
            onChange={handleChange}
            placeholder="Enter your mobile number"
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="dob">Date of Birth:</label>
          <input
            type="date"
            id="dob"
            name="dob"
            value={formData.dob}
            onChange={handleChange}
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="referral_by">Referral by:</label>
          <input
            type="text"
            id="referral_by"
            name="referral_by"
            value={formData.referral_by}
            onChange={handleChange}
            placeholder="Enter referral name"
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="android_version">Android Version:</label>
          <input
            type="text"
            id="android_version"
            name="android_version"
            value={formData.android_version}
            onChange={handleChange}
            placeholder="e.g., Android 13, iOS 16"
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="country">Country:</label>
          <input
            type="text"
            id="country"
            name="country"
            value={formData.country}
            onChange={handleChange}
            placeholder="e.g., IN, US"
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="gender">Gender:</label>
          <select id="gender" name="gender" value={formData.gender} onChange={handleChange} required>
            <option value="">Select Gender</option>
            <option value="Male">Male</option>
            <option value="Female">Female</option>
            <option value="OTHER">OTHER</option>
          </select>
        </div>

        <div className="form-group consent-group" style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', marginTop: '15px', marginBottom: '15px' }}>
          <input
            type="checkbox"
            id="consent"
            name="consent"
            checked={consentAccepted}
            onChange={(e) => setConsentAccepted(e.target.checked)}
            required
            style={{ marginTop: '5px' }}
          />
          <label htmlFor="consent" style={{ fontSize: '0.9rem', lineHeight: '1.4', fontWeight: 'normal' }}>
            By submitting this form to Frinksyn, you voluntarily consent to participate in AI and machine learning research and development by providing authentic personal image data, including current front-camera neutral selfies, head-pose captures, and optional historical gallery photos. You declare that all registration details, images, and required valid EXIF DateTimeOriginal metadata for current captures are true, original, and free from AI-generation, heavy filters, screenshots, or duplication. Participation is voluntary and coercion-free, with acknowledgment that non-compliant submissions may be rejected following quality review.
          </label>
        </div>

        {error && <div className="error-message">{error}</div>}

        <button type="submit" disabled={loading || !consentAccepted} className={`submit-btn ${!consentAccepted ? 'disabled-btn' : ''}`}>
          {loading ? 'Registering...' : 'Register'}
        </button>
      </form>
      <p style={{ marginTop: '1rem', textAlign: 'center' }}>
        Already have an account? <Link to="/login">Login here</Link>
      </p>
    </div>
  );
};

export default Registration;
